import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  Box, Button, Card, CardContent, Chip, CircularProgress, Divider,
  FormControl, IconButton, InputLabel, MenuItem, Select, Stack,
  Tab, Tabs, TextField, Tooltip, Typography, Alert, LinearProgress,
  Paper, Badge,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PublishIcon from '@mui/icons-material/Publish';
import UnpublishedIcon from '@mui/icons-material/Unpublished';
import ClearIcon from '@mui/icons-material/Clear';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import UnfoldLessIcon from '@mui/icons-material/UnfoldLess';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import MenuIcon from '@mui/icons-material/Menu';
import MenuOpenIcon from '@mui/icons-material/MenuOpen';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import TableChartIcon from '@mui/icons-material/TableChart';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import { useNavigate, useParams } from 'react-router-dom';
import { InlineMath, BlockMath } from 'react-katex';
import 'katex/dist/katex.min.css';
import { Facilitator } from './facilitator';
import {
  ExamPaper, ExamQuestion, ExamOption, QuestionType,
  examPaperService,
} from '../common/services/examPaperService';
import { exportSingleExamPaperToExcel, exportSingleExamPaperToPdf } from '../common/services/examPaperExport';

// -- MathText ----------------------------------------------------------------

const MathText: React.FC<{ text: string; preview?: boolean }> = ({ text, preview = false }) => {
  if (!text) return null;
  const parts: React.ReactNode[] = [];
  const re = /\$\$([\s\S]+?)\$\$|\$([^$\n]+?)\$/g;
  let last = 0;
  let m: RegExpExecArray | null;
  let idx = 0;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) parts.push(<span key={idx++}>{text.slice(last, m.index)}</span>);
    if (m[1] !== undefined) {
      parts.push(
        <span key={idx++} style={{ display: 'block', overflowX: 'auto' }}>
          <BlockMath math={m[1]} errorColor="#cc0000" />
        </span>
      );
    } else {
      parts.push(<InlineMath key={idx++} math={m[2]} errorColor="#cc0000" />);
    }
    last = m.index + m[0].length;
  }
  if (last < text.length) parts.push(<span key={idx++}>{text.slice(last)}</span>);
  if (preview) return <>{parts}</>;
  return (
    <Box sx={{ px: 1.5, py: 1, borderRadius: 1, bgcolor: 'grey.50', border: '1px dashed', borderColor: 'divider', fontSize: 14, lineHeight: 1.7, wordBreak: 'break-word', mt: 0.75 }}>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.25, fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.5 }}>Preview</Typography>
      {parts}
    </Box>
  );
};

// -- ImageUploadField --------------------------------------------------------

const ImageUploadField: React.FC<{
  value: string | null;
  onChange: (url: string | null) => void;
  label?: string;
  compact?: boolean;
}> = ({ value, onChange, label = 'Image', compact = false }) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setError(''); setUploading(true);
    try { const url = await examPaperService.uploadImage(file); onChange(url); }
    catch { setError('Upload failed. Try again.'); }
    finally { setUploading(false); if (inputRef.current) inputRef.current.value = ''; }
  };
  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <input ref={inputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleFileChange} />
        <Button size="small" variant={value ? 'outlined' : 'contained'} color={value ? 'secondary' : 'primary'}
          startIcon={uploading ? <CircularProgress size={14} color="inherit" /> : <UploadFileIcon />}
          onClick={() => inputRef.current?.click()} disabled={uploading} sx={{ whiteSpace: 'nowrap', fontSize: 12 }}>
          {uploading ? 'Uploading...' : value ? `Change ${label}` : `Upload ${label}`}
        </Button>
        {value && (
          <Tooltip title={`Remove ${label}`}>
            <IconButton size="small" color="error" onClick={() => onChange(null)}><ClearIcon fontSize="small" /></IconButton>
          </Tooltip>
        )}
        {error && <Typography variant="caption" color="error">{error}</Typography>}
      </Box>
      {uploading && <LinearProgress sx={{ mt: 0.5, borderRadius: 1 }} />}
      {value && (
        <Box sx={{ mt: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 0.75, display: 'inline-flex', maxWidth: compact ? 180 : 320 }}>
          <img src={value} alt={label} style={{ maxWidth: '100%', maxHeight: compact ? 72 : 150, objectFit: 'contain' }} />
        </Box>
      )}
    </Box>
  );
};

// -- Draft types -------------------------------------------------------------

interface DraftOption { optionLabel: string; optionText: string; optionImage: string | null; isCorrect: boolean; }
interface DraftQuestion {
  _key: number; questionNo: number; questionText: string; questionImage: string | null;
  marks: number; negativeMarks: number; questionType: QuestionType; options: DraftOption[];
}
type QStatus = 'complete' | 'warn' | 'empty';

function getStatus(q: DraftQuestion): QStatus {
  if (!q.questionText.trim()) return 'empty';
  if (!q.options.some(o => o.isCorrect)) return 'warn';
  if (q.options.some(o => !o.optionText.trim() && !o.optionImage)) return 'warn';
  return 'complete';
}

const OPTION_LABELS = ['A', 'B', 'C', 'D', 'E'];
const defaultOptions = (): DraftOption[] =>
  ['A', 'B', 'C', 'D'].map(label => ({ optionLabel: label, optionText: '', optionImage: null, isCorrect: false }));
const newQuestion = (key: number, no: number): DraftQuestion => ({
  _key: key, questionNo: no, questionText: '', questionImage: null, marks: 1, negativeMarks: 0, questionType: 0, options: defaultOptions(),
});
const fromApi = (q: ExamQuestion, key: number): DraftQuestion => ({
  _key: key, questionNo: q.questionNo, questionText: q.questionText, questionImage: q.questionImage,
  marks: q.marks, negativeMarks: q.negativeMarks, questionType: q.questionType,
  options: q.options.map(o => ({ optionLabel: o.optionLabel, optionText: o.optionText ?? '', optionImage: o.optionImage, isCorrect: o.isCorrect })),
});
const plainSummary = (text: string) =>
  text.replace(/\$\$[\s\S]+?\$\$/g, '[formula]').replace(/\$[^$\n]+?\$/g, '[formula]').slice(0, 90);

// -- QuestionPreview ---------------------------------------------------------

const QuestionPreview: React.FC<{ q: DraftQuestion; index: number }> = ({ q, index }) => {
  const typeLabel = q.questionType === 0 ? 'MCQ' : q.questionType === 1 ? 'Multi-select' : 'True / False';
  return (
    <Box sx={{ p: 2.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 2 }}>
        <Box sx={{ flexGrow: 1 }}>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', mb: 0.5 }}>
            <Typography component="span" sx={{ color: 'primary.main', fontWeight: 800, fontSize: 15, flexShrink: 0 }}>Q{index + 1}.</Typography>
            <Box component="span" sx={{ fontWeight: 600, fontSize: 15, lineHeight: 1.7, '& .katex': { fontSize: '1em' } }}>
              {q.questionText
                ? <MathText text={q.questionText} preview />
                : <Box component="span" sx={{ color: 'text.disabled', fontStyle: 'italic', fontWeight: 400 }}>No question text yet</Box>}
            </Box>
          </Box>
        </Box>
        <Stack direction="row" spacing={0.5} sx={{ flexShrink: 0 }}>
          <Chip label={typeLabel} size="small" color="primary" variant="outlined" sx={{ fontSize: 10 }} />
          <Chip label={`${q.marks}M`} size="small" color="info" variant="outlined" sx={{ fontSize: 10 }} />
          {q.negativeMarks > 0 && <Chip label={`-${q.negativeMarks}`} size="small" color="error" variant="outlined" sx={{ fontSize: 10 }} />}
        </Stack>
      </Box>
      {q.questionImage && (
        <Box sx={{ mb: 2, maxWidth: 420 }}>
          <img src={q.questionImage} alt="Question" style={{ maxWidth: '100%', maxHeight: 220, objectFit: 'contain', borderRadius: 6, border: '1px solid #e0e0e0' }} />
        </Box>
      )}
      <Stack spacing={1}>
        {q.options.map(opt => (
          <Box key={opt.optionLabel} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, p: 1.25, borderRadius: 1.5, border: '1.5px solid', borderColor: opt.isCorrect ? 'success.main' : 'divider', bgcolor: opt.isCorrect ? 'rgba(46,125,50,0.05)' : 'background.paper' }}>
            <Box sx={{ width: 26, height: 26, borderRadius: '50%', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 12, bgcolor: opt.isCorrect ? 'success.main' : 'grey.200', color: opt.isCorrect ? 'white' : 'text.secondary' }}>
              {opt.optionLabel}
            </Box>
            <Box sx={{ flexGrow: 1, '& .katex': { fontSize: '0.95em' } }}>
              {opt.optionText
                ? <Box component="span" sx={{ fontSize: 14 }}><MathText text={opt.optionText} preview /></Box>
                : opt.optionImage ? null
                  : <Typography variant="body2" color="text.disabled" fontStyle="italic">Empty</Typography>}
              {opt.optionImage && <img src={opt.optionImage} alt={opt.optionLabel} style={{ maxWidth: 160, maxHeight: 80, objectFit: 'contain', marginTop: 4, borderRadius: 4, display: 'block' }} />}
            </Box>
            {opt.isCorrect && <Chip label="Correct" size="small" color="success" sx={{ fontSize: 10, height: 20, fontWeight: 700, flexShrink: 0 }} />}
          </Box>
        ))}
      </Stack>
    </Box>
  );
};

// -- QuestionCard ------------------------------------------------------------

const QuestionCard = React.forwardRef<HTMLDivElement, {
  q: DraftQuestion; index: number; open: boolean; isFirst: boolean; isLast: boolean;
  onToggle: () => void; onChange: (updated: DraftQuestion) => void; onDelete: () => void;
  onMoveUp: () => void; onMoveDown: () => void;
}>(({ q, index, open, isFirst, isLast, onToggle, onChange, onDelete, onMoveUp, onMoveDown }, ref) => {
  const [tab, setTab] = useState<0 | 1>(0);
  const setField = <K extends keyof DraftQuestion>(key: K, value: DraftQuestion[K]) => onChange({ ...q, [key]: value });
  const setOption = (i: number, patch: Partial<DraftOption>) =>
    onChange({ ...q, options: q.options.map((o, idx) => idx === i ? { ...o, ...patch } : o) });
  const addOption = () => {
    if (q.options.length >= 5) return;
    const label = OPTION_LABELS[q.options.length];
    onChange({ ...q, options: [...q.options, { optionLabel: label, optionText: '', optionImage: null, isCorrect: false }] });
  };
  const removeOption = (i: number) => onChange({ ...q, options: q.options.filter((_, idx) => idx !== i) });
  const toggleCorrect = (i: number) => {
    if (q.questionType === 0 || q.questionType === 2) {
      onChange({ ...q, options: q.options.map((o, idx) => ({ ...o, isCorrect: idx === i })) });
    } else { setOption(i, { isCorrect: !q.options[i].isCorrect }); }
  };
  const handleTypeChange = (type: QuestionType) => {
    let opts = q.options;
    if (type === 2) {
      opts = [{ optionLabel: 'A', optionText: 'True', optionImage: null, isCorrect: false }, { optionLabel: 'B', optionText: 'False', optionImage: null, isCorrect: false }];
    } else if (q.questionType === 2) { opts = defaultOptions(); }
    if (type !== 1) { const first = opts.findIndex(o => o.isCorrect); opts = opts.map((o, i) => ({ ...o, isCorrect: i === first })); }
    onChange({ ...q, questionType: type, options: opts });
  };
  const isTrueFalse = q.questionType === 2;
  const isMulti = q.questionType === 1;
  const status = getStatus(q);
  const correctCount = q.options.filter(o => o.isCorrect).length;
  const statusIcon = status === 'complete'
    ? <CheckCircleIcon sx={{ fontSize: 16, color: 'success.main' }} />
    : status === 'warn' ? <WarningAmberIcon sx={{ fontSize: 16, color: 'warning.main' }} />
      : <ErrorOutlineIcon sx={{ fontSize: 16, color: 'error.light' }} />;

  return (
    <Card ref={ref} variant="outlined" sx={{
      mb: 1.5, borderRadius: 2, scrollMarginTop: 90,
      borderColor: open ? 'primary.light' : status === 'complete' ? 'rgba(46,125,50,0.25)' : status === 'warn' ? 'rgba(237,108,2,0.25)' : 'divider',
      boxShadow: open ? '0 2px 12px rgba(25,118,210,0.1)' : 'none',
      transition: 'box-shadow 0.2s, border-color 0.2s',
    }}>
      {/* Header */}
      <Box onClick={onToggle} sx={{
        px: 2, py: open ? 1.5 : 1.25,
        display: 'flex', alignItems: 'center', gap: 1.5, cursor: 'pointer',
        bgcolor: open ? 'primary.50' : 'grey.50',
        borderBottom: open ? '1px solid' : 'none', borderColor: 'divider',
        '&:hover': { bgcolor: open ? 'primary.50' : 'grey.100' },
        userSelect: 'none', transition: 'background-color 0.15s',
      }}>
        <Box sx={{ width: 30, height: 30, borderRadius: '50%', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 13, bgcolor: open ? 'primary.main' : 'grey.300', color: open ? 'white' : 'text.secondary', transition: 'all 0.15s' }}>
          {index + 1}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>{statusIcon}</Box>
        <Typography variant="body2" fontWeight={open ? 700 : 500} sx={{ flexGrow: 1, fontSize: 13, color: q.questionText ? 'text.primary' : 'text.disabled', fontStyle: q.questionText ? 'normal' : 'italic', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {q.questionText ? plainSummary(q.questionText) : 'New question — click to expand'}
        </Typography>
        <Stack direction="row" spacing={0.5} sx={{ flexShrink: 0 }}>
          <Chip size="small" label={q.questionType === 0 ? 'MCQ' : q.questionType === 1 ? 'Multi' : 'T/F'} variant="outlined" sx={{ fontSize: 10 }} />
          <Chip size="small" label={`${q.marks}M`} variant="outlined" sx={{ fontSize: 10 }} />
          {q.negativeMarks > 0 && <Chip size="small" label={`-${q.negativeMarks}`} color="error" variant="outlined" sx={{ fontSize: 10 }} />}
          <Chip size="small" label={`${correctCount}/${q.options.length} correct`} color={correctCount > 0 ? 'success' : 'warning'} variant={correctCount > 0 ? 'filled' : 'outlined'} sx={{ fontSize: 10 }} />
        </Stack>
        <Tooltip title="Move up">
          <span>
            <IconButton size="small" onClick={e => { e.stopPropagation(); onMoveUp(); }} disabled={isFirst} sx={{ flexShrink: 0, opacity: isFirst ? 0.3 : 1 }}>
              <ArrowUpwardIcon sx={{ fontSize: 15 }} />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Move down">
          <span>
            <IconButton size="small" onClick={e => { e.stopPropagation(); onMoveDown(); }} disabled={isLast} sx={{ flexShrink: 0, opacity: isLast ? 0.3 : 1 }}>
              <ArrowDownwardIcon sx={{ fontSize: 15 }} />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Delete question">
          <IconButton size="small" color="error" onClick={e => { e.stopPropagation(); onDelete(); }} sx={{ flexShrink: 0 }}>
            <DeleteIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
        {open ? <ExpandLessIcon fontSize="small" sx={{ flexShrink: 0, color: 'text.secondary' }} /> : <ExpandMoreIcon fontSize="small" sx={{ flexShrink: 0, color: 'text.secondary' }} />}
      </Box>

      {open && (
        <>
          <Box sx={{ borderBottom: '1px solid', borderColor: 'divider' }}>
            <Tabs value={tab} onChange={(_, v) => setTab(v)} textColor="primary" indicatorColor="primary" sx={{ minHeight: 36, '& .MuiTab-root': { minHeight: 36, fontSize: 12, py: 0 } }}>
              <Tab label="Edit" />
              <Tab label="Preview" />
            </Tabs>
          </Box>

          {tab === 0 && (
            <CardContent sx={{ pt: 2 }}>
              <Stack spacing={2.5}>
                <Stack direction="row" spacing={2} flexWrap="wrap">
                  <FormControl size="small" sx={{ minWidth: 200 }}>
                    <InputLabel>Question Type</InputLabel>
                    <Select label="Question Type" value={q.questionType} onChange={e => handleTypeChange(Number(e.target.value) as QuestionType)}>
                      <MenuItem value={0}>MCQ (single correct)</MenuItem>
                      <MenuItem value={1}>Multi-select</MenuItem>
                      <MenuItem value={2}>True / False</MenuItem>
                    </Select>
                  </FormControl>
                  <TextField label="Marks" type="number" size="small" sx={{ width: 110 }} value={q.marks} onChange={e => setField('marks', Math.max(0.5, Number(e.target.value)))} inputProps={{ min: 0.5, step: 0.5 }} />
                  <TextField label="Negative Marks" type="number" size="small" sx={{ width: 140 }} value={q.negativeMarks} onChange={e => setField('negativeMarks', Math.max(0, Number(e.target.value)))} inputProps={{ min: 0, step: 0.25 }} />
                </Stack>
                <Box>
                  <TextField label="Question Text *" fullWidth multiline minRows={2} size="small" value={q.questionText} onChange={e => setField('questionText', e.target.value)} placeholder="Plain text and/or LaTeX: e.g. Find $x^2 + y^2$ when..." helperText="Use $...$ for inline math, $$...$$ for block math" />
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>Question Image (optional)</Typography>
                  <ImageUploadField label="Question Image" value={q.questionImage} onChange={url => setField('questionImage', url)} />
                </Box>
                <Divider>
                  <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                    {isMulti ? 'Options — select all correct' : 'Options — click circle to mark the correct answer'}
                  </Typography>
                </Divider>
                <Stack spacing={1.5}>
                  {q.options.map((opt, oi) => (
                    <Box key={opt.optionLabel} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, p: 1.25, borderRadius: 1.5, border: '1.5px solid', borderColor: opt.isCorrect ? 'success.main' : 'divider', bgcolor: opt.isCorrect ? 'rgba(46,125,50,0.04)' : 'background.paper', transition: 'border-color 0.15s, background-color 0.15s' }}>
                      <Tooltip title={isMulti ? 'Toggle correct' : 'Mark as correct answer'}>
                        <Box onClick={() => toggleCorrect(oi)} sx={{ width: 28, height: 28, borderRadius: '50%', flexShrink: 0, mt: 0.5, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 13, cursor: 'pointer', bgcolor: opt.isCorrect ? 'success.main' : 'grey.200', color: opt.isCorrect ? 'white' : 'text.secondary', '&:hover': { bgcolor: opt.isCorrect ? 'success.dark' : 'grey.300' }, transition: 'background-color 0.15s' }}>
                          {opt.optionLabel}
                        </Box>
                      </Tooltip>
                      <Box sx={{ flexGrow: 1 }}>
                        <TextField size="small" fullWidth label={`Option ${opt.optionLabel} text`} value={opt.optionText} onChange={e => setOption(oi, { optionText: e.target.value })} disabled={isTrueFalse} placeholder={isTrueFalse ? '' : 'Supports LaTeX'} />
                        {!isTrueFalse && (
                          <Box sx={{ mt: 0.75 }}>
                            <ImageUploadField label="Option Image" value={opt.optionImage} onChange={url => setOption(oi, { optionImage: url })} compact />
                          </Box>
                        )}
                      </Box>
                      {!isTrueFalse && q.options.length > 2 && (
                        <Tooltip title="Remove option">
                          <IconButton size="small" color="error" onClick={() => removeOption(oi)} sx={{ mt: 0.25 }}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  ))}
                </Stack>
                {!isTrueFalse && q.options.length < 5 && (
                  <Button size="small" startIcon={<AddIcon />} onClick={addOption} variant="outlined" sx={{ alignSelf: 'flex-start', borderStyle: 'dashed' }}>Add Option</Button>
                )}
              </Stack>
            </CardContent>
          )}
          {tab === 1 && <QuestionPreview q={q} index={index} />}
        </>
      )}
    </Card>
  );
});

// -- Question Navigator (sidebar) --------------------------------------------

const QuestionNavRow: React.FC<{ q: DraftQuestion; idx: number; isOpen: boolean; onJump: (idx: number) => void }> = ({ q, idx, isOpen, onJump }) => {
  const st = getStatus(q);
  const statusIcon = st === 'complete'
    ? <CheckCircleIcon sx={{ fontSize: 13, color: 'success.main', flexShrink: 0 }} />
    : st === 'warn' ? <WarningAmberIcon sx={{ fontSize: 13, color: 'warning.main', flexShrink: 0 }} />
      : <ErrorOutlineIcon sx={{ fontSize: 13, color: 'error.light', flexShrink: 0 }} />;
  return (
    <Box onClick={() => onJump(idx)} sx={{
      px: 1.5, py: 0.875, display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer',
      borderBottom: '1px solid', borderColor: 'divider',
      bgcolor: isOpen ? 'primary.50' : 'background.paper',
      borderLeft: '3px solid',
      borderLeftColor: isOpen ? 'primary.main' : 'transparent',
      '&:hover': { bgcolor: isOpen ? 'primary.50' : 'grey.50' },
      transition: 'background-color 0.1s',
    }}>
      <Box sx={{ width: 22, height: 22, borderRadius: '50%', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 11, bgcolor: isOpen ? 'primary.main' : 'grey.200', color: isOpen ? 'white' : 'text.secondary', transition: 'all 0.15s' }}>
        {idx + 1}
      </Box>
      {statusIcon}
      <Typography variant="caption" sx={{ flexGrow: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontWeight: isOpen ? 600 : 400, color: q.questionText ? 'text.primary' : 'text.disabled', fontStyle: q.questionText ? 'normal' : 'italic', fontSize: 12 }}>
        {q.questionText ? plainSummary(q.questionText) : 'Empty'}
      </Typography>
      <Chip size="small" label={q.questionType === 0 ? 'MCQ' : q.questionType === 1 ? 'M' : 'T/F'} variant="outlined" sx={{ fontSize: 9, height: 16, px: 0, flexShrink: 0, '& .MuiChip-label': { px: 0.75 } }} />
    </Box>
  );
};

const QuestionListPanel: React.FC<{
  questions: DraftQuestion[];
  openKeys: Set<number>;
  onJump: (idx: number) => void;
  onClose: () => void;
}> = ({ questions, openKeys, onJump, onClose }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
    <Box sx={{ px: 2, py: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'grey.50', flexShrink: 0 }}>
      <Typography variant="subtitle2" fontWeight={700} sx={{ fontSize: 12 }}>{questions.length} Questions</Typography>
      <Tooltip title="Hide panel">
        <IconButton size="small" onClick={onClose}><MenuOpenIcon fontSize="small" /></IconButton>
      </Tooltip>
    </Box>
    <Box sx={{ overflowY: 'auto', flexGrow: 1 }}>
      {questions.map((q, idx) => (
        <QuestionNavRow key={q._key} q={q} idx={idx} isOpen={openKeys.has(q._key)} onJump={onJump} />
      ))}
    </Box>
  </Box>
);

// -- ExamPaperBuilder --------------------------------------------------------

export const ExamPaperBuilder: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const paperId = Number(id);
  const navigate = useNavigate();

  const [paper, setPaper] = useState<ExamPaper | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [questions, setQuestions] = useState<DraftQuestion[]>([]);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [saved, setSaved] = useState(false);
  const [toggling, setToggling] = useState(false);
  const [toggleError, setToggleError] = useState('');
  const [exportingPdf, setExportingPdf] = useState(false);
  const [keyCounter, setKeyCounter] = useState(0);
  const [openKeys, setOpenKeys] = useState<Set<number>>(new Set());
  const [isDirty, setIsDirty] = useState(false);
  const [navOpen, setNavOpen] = useState(true);
  const cardRefs = useRef<Map<number, HTMLDivElement>>(new Map());
  const handleSaveRef = useRef<() => void>(() => {});

  const load = useCallback(async () => {
    setLoading(true); setLoadError('');
    try {
      const p = await examPaperService.getById(paperId);
      setPaper(p);
      let k = 0;
      setQuestions(p.questions.map(q => fromApi(q, k++)));
      setKeyCounter(k);
      setOpenKeys(new Set());
      setIsDirty(false);
    } catch { setLoadError('Failed to load exam paper.'); }
    finally { setLoading(false); }
  }, [paperId]);

  useEffect(() => { load(); }, [load]);

  const markDirty = () => setIsDirty(true);

  const addQuestion = () => {
    const key = keyCounter;
    setQuestions(qs => [...qs, newQuestion(key, qs.length + 1)]);
    setKeyCounter(k => k + 1);
    setOpenKeys(prev => new Set([...Array.from(prev), key]));
    markDirty();
    setTimeout(() => cardRefs.current.get(key)?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
  };

  const updateQuestion = (idx: number, updated: DraftQuestion) => {
    setQuestions(qs => qs.map((q, i) => i === idx ? updated : q));
    markDirty();
  };

  const deleteQuestion = (idx: number) => {
    const key = questions[idx]._key;
    setQuestions(qs => qs.filter((_, i) => i !== idx).map((q, i) => ({ ...q, questionNo: i + 1 })));
    setOpenKeys(prev => { const s = new Set(prev); s.delete(key); return s; });
    markDirty();
  };

  const toggleOpen = (key: number) =>
    setOpenKeys(prev => { const s = new Set(prev); s.has(key) ? s.delete(key) : s.add(key); return s; });

  const expandAll = () => setOpenKeys(new Set(questions.map(q => q._key)));
  const collapseAll = () => setOpenKeys(new Set());

  const jumpToQuestion = (idx: number) => {
    const q = questions[idx];
    setOpenKeys(prev => new Set([...Array.from(prev), q._key]));
    setTimeout(() => cardRefs.current.get(q._key)?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
  };

  const handleSave = async () => {
    for (let i = 0; i < questions.length; i++) {
      const q = questions[i];
      if (!q.questionText.trim()) { setSaveError(`Q${i + 1}: question text is required.`); return; }
      if (q.options.length < 2) { setSaveError(`Q${i + 1}: at least 2 options required.`); return; }
      if (!q.options.some(o => o.isCorrect)) { setSaveError(`Q${i + 1}: mark at least one correct answer.`); return; }
      for (const o of q.options) {
        if (!o.optionText.trim() && !o.optionImage) { setSaveError(`Q${i + 1} Option ${o.optionLabel}: text or image required.`); return; }
      }
    }
    setSaving(true); setSaveError(''); setSaved(false);
    try {
      const payload = questions.map(q => ({
        questionNo: q.questionNo, questionText: q.questionText, questionImage: q.questionImage,
        marks: q.marks, negativeMarks: q.negativeMarks, questionType: q.questionType,
        options: q.options.map(o => ({ optionLabel: o.optionLabel, optionText: o.optionText || null, optionImage: o.optionImage, isCorrect: o.isCorrect })),
      }));
      const updated = await examPaperService.saveQuestions(paperId, payload);
      setPaper(updated); setSaved(true); setIsDirty(false);
      setTimeout(() => setSaved(false), 3000);
    } catch { setSaveError('Failed to save. Please try again.'); }
    finally { setSaving(false); }
  };

  // Keep a stable ref so the Ctrl+S handler always calls the latest handleSave
  useEffect(() => { handleSaveRef.current = handleSave; });
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 's') { e.preventDefault(); handleSaveRef.current(); }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);
  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if (isDirty) { e.preventDefault(); e.returnValue = ''; }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isDirty]);

  const moveQuestion = (idx: number, dir: -1 | 1) => {
    setQuestions(qs => {
      const next = [...qs];
      const target = idx + dir;
      if (target < 0 || target >= next.length) return qs;
      [next[idx], next[target]] = [next[target], next[idx]];
      return next.map((q, i) => ({ ...q, questionNo: i + 1 }));
    });
    markDirty();
  };

  const handleToggleRelease = async () => {
    if (!paper) return;
    setToggling(true); setToggleError('');
    try {
      if (paper.status === 1) { await examPaperService.retract(paperId); setPaper(p => p ? { ...p, status: 0, statusLabel: 'Draft' } : p); }
      else { await examPaperService.release(paperId); setPaper(p => p ? { ...p, status: 1, statusLabel: 'Released' } : p); }
    } catch { setToggleError('Failed to update release status. Please try again.'); } finally { setToggling(false); }
  };

  const totalMarks = questions.reduce((s, q) => s + q.marks, 0);
  const completeCount = useMemo(() => questions.filter(q => getStatus(q) === 'complete').length, [questions]);
  const warnCount = questions.filter(q => getStatus(q) === 'warn').length;
  const emptyCount = questions.filter(q => getStatus(q) === 'empty').length;

  return (
    <Facilitator>
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
        {loading ? (
          <Box display="flex" justifyContent="center" pt={12}><CircularProgress /></Box>
        ) : loadError ? (
          <Box sx={{ p: 3 }}><Alert severity="error" action={<Button size="small" onClick={load}>Retry</Button>}>{loadError}</Alert></Box>
        ) : paper && (
          <>
            {/* Sticky header */}
            <Paper elevation={2} sx={{ position: 'sticky', top: 0, zIndex: 100, px: 2.5, py: 1.5, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', borderRadius: 0, borderBottom: '1px solid', borderColor: 'divider' }}>
              <IconButton size="small" onClick={() => navigate('/Facilitator/ExamPapers')}><ArrowBackIcon /></IconButton>
              {questions.length > 0 && (
                <Tooltip title={navOpen ? 'Hide question list' : 'Show question list'}>
                  <IconButton size="small" onClick={() => setNavOpen(v => !v)}>
                    {navOpen ? <MenuOpenIcon /> : <MenuIcon />}
                  </IconButton>
                </Tooltip>
              )}
              <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <Typography variant="h6" fontWeight={700} noWrap>{paper.title}</Typography>
                  <Chip size="small" label={paper.status === 1 ? 'Released' : 'Draft'} color={paper.status === 1 ? 'success' : 'default'} variant={paper.status === 1 ? 'filled' : 'outlined'} sx={{ fontWeight: 600, flexShrink: 0 }} />
                  {isDirty && <Chip size="small" label="Unsaved changes" color="warning" variant="outlined" sx={{ fontSize: 10, flexShrink: 0 }} />}
                </Box>
                <Typography variant="caption" color="text.secondary">
                  {paper.examName}{paper.subject ? ` · ${paper.subject}` : ''} · {paper.durationMins} min
                  &nbsp;·&nbsp;
                  <Box component="span" sx={{ color: completeCount === questions.length && questions.length > 0 ? 'success.main' : 'inherit', fontWeight: 600 }}>{completeCount}/{questions.length} complete</Box>
                  &nbsp;·&nbsp; {totalMarks} marks
                </Typography>
              </Box>
              <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
                <Tooltip title="Export to Excel">
                  <span>
                    <Button
                      variant="outlined" size="small"
                      startIcon={<TableChartIcon />}
                      disabled={questions.length === 0}
                      onClick={() => exportSingleExamPaperToExcel(paper, questions)}
                      sx={{ color: 'success.dark', borderColor: 'success.main', '&:hover': { borderColor: 'success.dark', bgcolor: 'rgba(46,125,50,0.06)' } }}
                    >
                      Excel
                    </Button>
                  </span>
                </Tooltip>
                <Tooltip title="Export to PDF">
                  <span>
                    <Button
                      variant="outlined" size="small"
                      startIcon={exportingPdf ? <CircularProgress size={14} /> : <PictureAsPdfIcon />}
                      disabled={questions.length === 0 || exportingPdf}
                      onClick={async () => { setExportingPdf(true); await exportSingleExamPaperToPdf(paper, questions); setExportingPdf(false); }}
                      sx={{ color: 'error.dark', borderColor: 'error.main', '&:hover': { borderColor: 'error.dark', bgcolor: 'rgba(211,47,47,0.06)' } }}
                    >
                      {exportingPdf ? 'Generating...' : 'PDF'}
                    </Button>
                  </span>
                </Tooltip>
                <Tooltip title={paper.status === 0 && isDirty ? 'Save your changes before releasing' : ''}>
                  <span>
                    <Button variant="outlined" size="small" color={paper.status === 1 ? 'warning' : 'success'}
                      startIcon={toggling ? <CircularProgress size={14} /> : paper.status === 1 ? <UnpublishedIcon /> : <PublishIcon />}
                      onClick={handleToggleRelease} disabled={toggling || (paper.status === 0 && isDirty)}>
                      {paper.status === 1 ? 'Retract' : 'Release'}
                    </Button>
                  </span>
                </Tooltip>
                <Badge color="warning" variant="dot" invisible={!isDirty}>
                  <Button variant="contained" size="small"
                    startIcon={saving ? <CircularProgress size={14} sx={{ color: 'white' }} /> : <SaveIcon />}
                    onClick={handleSave} disabled={saving} color={saved ? 'success' : 'primary'}>
                    {saving ? 'Saving...' : saved ? 'Saved!' : 'Save All'}
                  </Button>
                </Badge>
              </Stack>
            </Paper>

            {saveError && <Box sx={{ px: 2, pt: 1.5 }}><Alert severity="error" onClose={() => setSaveError('')}>{saveError}</Alert></Box>}
            {toggleError && <Box sx={{ px: 2, pt: 1.5 }}><Alert severity="error" onClose={() => setToggleError('')}>{toggleError}</Alert></Box>}

            {/* Two-column body */}
            <Box sx={{ display: 'flex', flexGrow: 1, overflow: 'hidden' }}>
              {/* Sidebar navigator */}
              {navOpen && questions.length > 0 && (
                <Box sx={{ width: 260, flexShrink: 0, borderRight: '1px solid', borderColor: 'divider', display: 'flex', flexDirection: 'column', overflow: 'hidden', bgcolor: 'background.paper' }}>
                  <QuestionListPanel questions={questions} openKeys={openKeys} onJump={jumpToQuestion} onClose={() => setNavOpen(false)} />
                </Box>
              )}

              {/* Main content */}
              <Box sx={{ flexGrow: 1, overflowY: 'auto', p: 2 }}>
                {/* Toolbar bar */}
                {questions.length > 0 && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                    <Stack direction="row" spacing={2}>
                      {completeCount > 0 && <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'center' }}><CheckCircleIcon sx={{ fontSize: 14, color: 'success.main' }} /><Typography variant="caption" color="success.main" fontWeight={600}>{completeCount} complete</Typography></Box>}
                      {warnCount > 0 && <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'center' }}><WarningAmberIcon sx={{ fontSize: 14, color: 'warning.main' }} /><Typography variant="caption" color="warning.main" fontWeight={600}>{warnCount} needs attention</Typography></Box>}
                      {emptyCount > 0 && <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'center' }}><ErrorOutlineIcon sx={{ fontSize: 14, color: 'error.light' }} /><Typography variant="caption" color="error.light" fontWeight={600}>{emptyCount} empty</Typography></Box>}
                    </Stack>
                    <Box sx={{ flexGrow: 1 }} />
                    <Button size="small" variant="outlined" startIcon={<UnfoldMoreIcon />} onClick={expandAll} sx={{ fontSize: 11 }}>Expand All</Button>
                    <Button size="small" variant="outlined" startIcon={<UnfoldLessIcon />} onClick={collapseAll} sx={{ fontSize: 11 }}>Collapse All</Button>
                    <Button size="small" variant="contained" startIcon={<AddIcon />} onClick={addQuestion}>Add Question</Button>
                  </Box>
                )}

                {questions.length === 0 && (
                  <Paper variant="outlined" sx={{ p: 6, textAlign: 'center', color: 'text.secondary', mt: 2 }}>
                    <Typography variant="body1" mb={2}>No questions yet. Add the first one to get started.</Typography>
                    <Button variant="contained" startIcon={<AddIcon />} onClick={addQuestion}>Add Question</Button>
                  </Paper>
                )}

                {questions.map((q, idx) => (
                  <QuestionCard
                    key={q._key}
                    ref={el => { if (el) cardRefs.current.set(q._key, el); else cardRefs.current.delete(q._key); }}
                    q={q} index={idx}
                    open={openKeys.has(q._key)}
                    isFirst={idx === 0}
                    isLast={idx === questions.length - 1}
                    onToggle={() => toggleOpen(q._key)}
                    onChange={updated => updateQuestion(idx, updated)}
                    onDelete={() => deleteQuestion(idx)}
                    onMoveUp={() => moveQuestion(idx, -1)}
                    onMoveDown={() => moveQuestion(idx, 1)}
                  />
                ))}

                {questions.length > 0 && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2, pb: 2 }}>
                    <Button variant="outlined" startIcon={<AddIcon />} onClick={addQuestion}>Add Question</Button>
                    <Badge color="warning" variant="dot" invisible={!isDirty}>
                      <Button variant="contained" startIcon={saving ? <CircularProgress size={14} sx={{ color: 'white' }} /> : <SaveIcon />} onClick={handleSave} disabled={saving} color={saved ? 'success' : 'primary'}>
                        {saving ? 'Saving...' : saved ? 'Saved!' : 'Save All'}
                      </Button>
                    </Badge>
                  </Box>
                )}
              </Box>
            </Box>
          </>
        )}
      </Box>
    </Facilitator>
  );
};
